import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';

import BasePageUAM from '../BasePageUAM/BasePageUAM';
import { getLoginURL } from '../../utils/redirect-utils';
import Step1RegistrationPanel from './Panels/Step1RegistrationPanel/Step1RegistrationPanel';
import EmailPendingPanel from '../../shared-panels/uam/EmailPendingPanel/EmailPendingPanel';
import RegistrationCompletePanel from '../../shared-panels/uam/RegistrationCompletePanel/RegistrationCompletePanel';
import { makeInitialField, setFieldAndValidate } from '../../utils/field-utils';
import { userFieldSchemas } from '../../utils/validation-utils';
import { composeDestinationQuerystring } from '../../../shared-with-server/destination-utils';
import { defaultCAccounts, defaultInstitutionId, destinationInfo } from '../../destructured-front-end-config';
import { getLocalStorageRegistrationInfo, multiStepResultTypes, processMultiStepRegistrationEntry } from '../../utils/uam-utils';
import BusyOverlay from '../../shared-panels/uam/BusyOverlay/BusyOverlay';

const CFA_CANDIDATE_ROLE = {
  value: 'CFA Candidate',
  label: 'CFA Candidate',
  type: 'Professional',
};

const RegistrationStep1Page = ({ brand, destPath }) => {
  const isNewRegistration = useRef(localStorage.getItem('is_new_registration') === 'true');

  const [isBusy, setIsBusy] = useState(!isNewRegistration.current);
  const [emailPending, setEmailPending] = useState(false);
  const [registrationComplete, setRegistrationComplete] = useState(false);
  const [firstName, setFirstName] = useState(makeInitialField(''));
  const [lastName, setLastName] = useState(makeInitialField(''));
  const [countryOrRegion, setCountryOrRegion] = useState(makeInitialField(''));
  const [role, setRole] = useState(makeInitialField(''));
  const [roleType, setRoleType] = useState('');
  const [availableRoles, setAvailableRoles] = useState([]);
  const [school, setSchool] = useState(makeInitialField({ institution: '', institutionId: '', cAccount: '' }));
  const [discipline, setDiscipline] = useState(makeInitialField(''));
  const [availableDisciplines, setAvailableDisciplines] = useState([]);

  const handleGoToLogin = () => {
    window.location.href = getLoginURL(destPath);
  };

  // empty effect to load data if not new registration and process results (only results that do NOT depend on role data)
  useEffect(() => {
    if (isNewRegistration.current) {
      const step1Data = getLocalStorageRegistrationInfo('step1');

      if (step1Data?.firstName?.length) {
        setFieldAndValidate(firstName, setFirstName, step1Data.firstName, userFieldSchemas.firstName);
      }

      if (step1Data?.lastName?.length) {
        setFieldAndValidate(lastName, setLastName, step1Data.lastName, userFieldSchemas.lastName);
      }

      if (step1Data?.country?.length) {
        setFieldAndValidate(countryOrRegion, setCountryOrRegion, step1Data.country, userFieldSchemas.countryOrRegion);
      }
    }

    axios.get('/api/v2/fi/user-roles').then((res) => {
      setAvailableRoles([...res.data, CFA_CANDIDATE_ROLE]);
    });

    axios.get('/api/v2/fi/instructor-disciplines').then((res) => {
      setAvailableDisciplines(res.data);
    });
  }, []);

  // Handle institution based on role type changes
  useEffect(() => {
    if (roleType === 'Professional') {
      setSchool({
        value: {
          institution: 'No Institution',
          institutionId: defaultInstitutionId,
          cAccount: defaultCAccounts?.[0] || '',
        },
        state: 2,
        error: '',
      });
    } else if (
      roleType
      && school.value?.institution === 'No Institution'
      && school.value?.institutionId === defaultInstitutionId
    ) {
      setSchool({
        value: { institution: '', institutionId: '', cAccount: '' },
        state: 0,
        error: '',
      });
    }
  }, [roleType]);

  // roles and disciplines data watcher to carry out update (from server or local storage)
  // for parts that depend on role and discipline data
  useEffect(() => {
    if (!availableRoles.length || !availableDisciplines.length) { return; }

    if (isBusy) {
      setIsBusy(false);
    }

    if (isNewRegistration.current) {
      const step1Data = getLocalStorageRegistrationInfo('step1');

      if (step1Data?.role?.length) {
        const matchedRole = availableRoles.find((roleEntry) => roleEntry.value === step1Data.role);
        setFieldAndValidate(role, setRole, matchedRole.value, userFieldSchemas.role);
        setRoleType(matchedRole.type);
      }

      if (step1Data?.institutionId?.length) {
        const schoolData = {
          institution: step1Data.school,
          institutionId: step1Data.institutionId,
          cAccount: step1Data.cAccount,
        };
        setFieldAndValidate(
          school,
          setSchool,
          schoolData,
          userFieldSchemas.institution,
        );
      }

      if (step1Data?.discipline?.length) {
        setFieldAndValidate(discipline, setDiscipline, step1Data.discipline, userFieldSchemas.discipline);
      }
    } else {
      axios.get(
        '/api/v2/fi/multi-step-registration',
        { validateStatus: (status) => status < 500 },
      ).then((registrationResult) => {
        if (registrationResult.status === 404) { // user not found - this is fine, they're starting fresh
          return;
        }

        if (registrationResult.status === 401) {
          // Try to refresh registration token
          const email = getLocalStorageRegistrationInfo('email');
          if (!email) {
            // No email in localStorage, user should start from email page
            return;
          }

          axios.post(`/api/v2/fi/multi-step-registration-entry?${composeDestinationQuerystring(destinationInfo, destPath)}`, {
            email,
          }, { validateStatus: (status) => status < 500 }).then((entryResult) => {
            const result = processMultiStepRegistrationEntry(entryResult.status, entryResult.data, setEmailPending, setRegistrationComplete);

            if (result.status === multiStepResultTypes.error) {
              // Don't show error for existing users who don't have registration data
              // They can just fill out the form fresh
              console.warn('Could not retrieve registration data, user can proceed with fresh registration', entryResult);
            }
          }).catch((err) => {
            // Don't show error for existing users who don't have registration data
            console.warn('Could not retrieve registration data, user can proceed with fresh registration', err);
          });

          return;
        }

        if (registrationResult.status !== 200) {
          // For non-200, non-404, non-401 responses, log but don't block the user
          // They can still fill out the form
          console.warn('Unexpected status when getting registration data, user can proceed', registrationResult.status);
          return;
        }

        const step1Data = registrationResult.data.step1;
        const skipDebounce = true;
        setFieldAndValidate(firstName, setFirstName, step1Data.firstName, userFieldSchemas.firstName, skipDebounce);
        setFieldAndValidate(lastName, setLastName, step1Data.lastName, userFieldSchemas.lastName, skipDebounce);
        setFieldAndValidate(countryOrRegion, setCountryOrRegion, step1Data.country, userFieldSchemas.countryOrRegion, skipDebounce);
        const matchedRole = availableRoles.find((roleEntry) => roleEntry.value === step1Data.role, skipDebounce);
        setFieldAndValidate(role, setRole, matchedRole?.value, userFieldSchemas.role, skipDebounce);
        setRoleType(matchedRole?.type);
        let schoolData;
        if (step1Data.institutionId) {
          schoolData = { institution: step1Data.school, institutionId: step1Data.institutionId, cAccount: step1Data.cAccount };
        } else {
          schoolData = { institution: '', institutionId: '', cAccount: '' };
        }
        setFieldAndValidate(school,
          setSchool,
          schoolData,
          userFieldSchemas.institution,
          skipDebounce);
        setFieldAndValidate(discipline, setDiscipline, step1Data.discipline, userFieldSchemas.discipline, skipDebounce);
      });
    }
  }, [availableRoles, availableDisciplines]);

  const getPanel = () => {
    if (emailPending) {
      return <EmailPendingPanel email={getLocalStorageRegistrationInfo('email')} />;
    }

    if (registrationComplete) {
      return <RegistrationCompletePanel email={getLocalStorageRegistrationInfo('email')} isPriorRegistration isEmailVerified={false} onGoToLogin={handleGoToLogin} />;
    }

    return (
      <>
        { isBusy && <BusyOverlay /> }
        <Step1RegistrationPanel
          brand={brand}
          destPath={destPath}
          firstName={firstName}
          setFirstName={setFirstName}
          lastName={lastName}
          setLastName={setLastName}
          countryOrRegion={countryOrRegion}
          setCountryOrRegion={setCountryOrRegion}
          role={role}
          setRole={setRole}
          availableRoles={availableRoles}
          roleType={roleType}
          setRoleType={setRoleType}
          school={school}
          setSchool={setSchool}
          discipline={discipline}
          setDiscipline={setDiscipline}
          availableDisciplines={availableDisciplines}
          setEmailPending={setEmailPending}
          setRegistrationComplete={setRegistrationComplete}
          heading={
            <h2>Tell us about yourself</h2>
          }
        />
      </>
    );
  };

  return (
    <BasePageUAM activeTab={2} brand={brand} destPath={destPath} needsDestPath>
      {
        getPanel()
      }
    </BasePageUAM>
  );
};

RegistrationStep1Page.propTypes = {
  brand: PropTypes.object.isRequired,
  destPath: PropTypes.object.isRequired,
};

export default RegistrationStep1Page;
